import { Component, EventEmitter, HostListener, inject, Input, OnInit, Output, SimpleChanges, ViewChild } from '@angular/core';
import { BehaviorSubject, firstValueFrom, Subject, Subscription } from 'rxjs';
import { CustomModalComponent } from 'src/app/components/custom-modal/custom-modal.component';
import { CustomTableSelectComponent } from 'src/app/components/custom-table-select/custom-table-select.component';
import { ActionColor, CheckBoxAction, TableAction } from 'src/app/interfaces/custom-table/custom-table-action.interface';
import { DevolutionProjectionModel } from 'src/app/models/preliminaries/devolution-projection';
import { DevolutionProjectionDetailModel } from 'src/app/models/preliminaries/devolution-projection-detail';
import { ColumnResponseModel } from 'src/app/models/responses/column-response.model';
import { PagedResponseModel } from 'src/app/models/responses/paged-response.model';
import { DevolutionProjectionCaptureTableModel } from 'src/app/models/tables/preliminaries/devolution-projection-capture-table.model';
import { RentalStockModalModel } from 'src/app/models/tables/rental/rental-stock-modal.model';
import { ConfirmDialogService } from 'src/app/services/confirm-dialog/confirm-dialog.service';
import { DevolutionProjectionDetailService } from 'src/app/services/preliminaries/devolution-projection-detail.service';
import { ToastHandlerService } from 'src/app/services/toast/toast-handler.service';

@Component({
  selector: 'app-devolution-projection-detail',
  templateUrl: './devolution-projection-detail.component.html',
  styleUrls: ['./devolution-projection-detail.component.scss'],
  standalone: false
})
export class DevolutionProjectionDetailComponent implements OnInit {

  private readonly confirmDialogService = inject(ConfirmDialogService);
  private readonly devolutionProjectionDetailService = inject(DevolutionProjectionDetailService);
  private readonly toastHandlerService = inject(ToastHandlerService);

  @ViewChild('articleSelection') articleForSelectionModalComponent?: CustomModalComponent | null = null;
  @Input() devolutionProjectionModel: DevolutionProjectionModel = new DevolutionProjectionModel();
  @Output() emitQuantityDetails = new EventEmitter<number>();

  ascendant: boolean = true;
  ascendantArticle: boolean = true;
  checkboxActions: CheckBoxAction[] = [
    {
      label: 'Eliminar',
      icon: 'pi pi-trash',
      color: ActionColor.DANGER,
      action: 'bulk-delete'
    },
  ];
  clearSelectionTrigger: number = 0;
  columnSort: string | null = null;
  columnSortArticle: string | null = null;
  component = CustomTableSelectComponent;
  dataArticle: PagedResponseModel<RentalStockModalModel> =
    new PagedResponseModel<RentalStockModalModel>();
  detailData: PagedResponseModel<DevolutionProjectionCaptureTableModel> =
    new PagedResponseModel<DevolutionProjectionCaptureTableModel>();
  detailHeader: ColumnResponseModel[] = [];
  devolutionProjectionDetail: DevolutionProjectionDetailModel = new DevolutionProjectionDetailModel();
  devolutionProjectionDetails: DevolutionProjectionDetailModel[] = [];
  disabledButton: boolean = false;
  filters: ColumnResponseModel[] = [];
  filtersArticle: ColumnResponseModel[] = [];
  headerArticle: ColumnResponseModel[] = [];
  inputsForArticlesSelection$ = new BehaviorSubject<any>({
    headers: [],
    data: [],
    showCheckbox: false,
    showRecover: true,
    isMultipleSelect: true,
    filters: [],
    actions: [],
  });
  isFormView: boolean = false;
  limit: number = 10;
  limitArticle: number = 5;
  output$ = new Subject<any>();
  page: number = 1;
  pageArticle: number = 1;
  quantityDetails: number = 0;
  subscription: Subscription;
  tableActions: TableAction[] = [
    {
      label: 'Editar',
      icon: 'pi pi-pencil',
      action: 'edit'
    }
  ];

  async ngOnInit() {
    await this.getColumnsForDetailCapture();
    this.subscription = this.output$.subscribe(({ type, payload }) => {
      switch (type) {
        case 'onPageChange':
          this.onPageChange(payload, 'selection');
          break;
        case 'onFilterChange':
          this.onFilterChange(payload);
          break;
        default:
          console.warn('Tipo de evento desconocido:', type);
      }
    });
  }

  async ngOnChanges(changes: SimpleChanges) {
    const devolutionProjectionModel = changes["devolutionProjectionModel"];
    if (devolutionProjectionModel && !devolutionProjectionModel.firstChange) {
      await this.getDevolutionProjectionDetailsForCapture();
      this.clearSelectionTrigger++;
      this.getFormView(false);
    }
  }

  @HostListener('window:beforeunload')
  beforeUnloadHandler() {
    this.ngOnDestroy();
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  async deleteDetail(selectedIds: number[]) {
    if (!selectedIds || selectedIds.length === 0) {
      this.toastHandlerService.showToast(
        'warning',
        'Debe seleccionar al menos un registro para eliminar.'
      );
      return;
    }
    const accepted = await this.confirmDialogService.confirm('¿Realmente desea eliminar los detalles seleccionados?');
    if (accepted) {
      try {
        await firstValueFrom(this.devolutionProjectionDetailService.deleteSomeDevolutionProjectionDetails(selectedIds));
        this.toastHandlerService.showToast(
          'success',
          'El detalle de la proyección de devolución fue eliminado exitosamente.'
        );
        await this.getDevolutionProjectionDetailsForCapture();
        this.clearSelectionTrigger++;
        this.getFormView(false);
      } catch (error) {
        console.error('Error al eliminar los detalles de la proyección de devolución:', error);
      }
    }
  }

  async getArticlesForSelection() {
    try {
      const { branchId, costCenterId } = this.devolutionProjectionModel;
      this.dataArticle = await firstValueFrom(this.devolutionProjectionDetailService.getArticlesForDetails(
        branchId, costCenterId, this.pageArticle, this.limitArticle, this.columnSortArticle, this.ascendantArticle,
        this.filtersArticle
      ));
      this.updateInputs();
    } catch (error) {
      console.error('Error al obtener la lista de los artículos.', error);
    }
  }

  async getColumnsForArticleSelection() {
    try {
      if (this.headerArticle.length === 0) {
        const response = await firstValueFrom(this.devolutionProjectionDetailService.getColumnsForArticles());
        this.headerArticle = response;
        this.updateInputs();
      }
    } catch (error) {
      console.error('Error al buscar las columnas para la selección de artículos.', error);
    }
  }

  async getColumnsForDetailCapture() {
    try {
      if (this.detailHeader.length === 0) {
        const response = await firstValueFrom(this.devolutionProjectionDetailService.getColumnsForDetailCapture());
        this.detailHeader = response;
      }
    } catch (error) {
      console.error('Error al obtener las columnas del detalle para la captura.', error);
    }
  }

  async getDevolutionProjectionDetailById(detailId: number) {
    try {
      const response = await firstValueFrom(this.devolutionProjectionDetailService.getDevolutionProjectionDetailById(detailId));
      this.devolutionProjectionDetail = response;
      this.getFormView(true);
    } catch (error) {
      console.error('Error al obtener el detalle de la proyección de devolución.', error);
    }
  }

  async getDevolutionProjectionDetailByKeys(devolutionProjectionId: number, articleId: number, storeId: number) {
    try {
      const response = await firstValueFrom(this.devolutionProjectionDetailService.getDevolutionProjectionDetailByKeys(
        devolutionProjectionId, articleId, storeId
      ));
      this.devolutionProjectionDetail = response;
      this.getFormView(true);
    } catch (error) {
      console.error('Error al obtener el detalle de la proyección de devolución.', error);
    }
  }

  async getDevolutionProjectionDetailsForCapture() {
    try {
      const response = await firstValueFrom(this.devolutionProjectionDetailService.getDetailsForCapture(this.page, this.limit,
        this.columnSort, this.ascendant, this.filters, this.devolutionProjectionModel.devolutionProjectionId));
      this.detailData = response;
      this.quantityDetails = response.totalItems;
      this.emitQuantityDetails.emit(response.totalItems);
    } catch (error) {
      console.error('Error al obtener los detalles de la proyección de devolución para la captura de datos.', error);
    }
  }

  getFormView(isFormView: boolean) {
    this.isFormView = isFormView;
  }

  async handleSelectedItem(selectedItems: RentalStockModalModel[]) {
    const devolutionProjectionId = this.devolutionProjectionModel.devolutionProjectionId;
    if (selectedItems.length === 1) {
      const [item] = selectedItems;
      await this.getDevolutionProjectionDetailByKeys(devolutionProjectionId, item.articleId, item.storeId);
    }
    if (selectedItems.length !== 1) {
      this.devolutionProjectionDetails = selectedItems.map((item) => {
        const detail = new DevolutionProjectionDetailModel();
        detail.articleId = item.articleId;
        detail.devolutionProjectionId = devolutionProjectionId;
        detail.quantity = item.stock;
        detail.storeId = item.storeId;
        return detail;
      });
    }
  }

  async onAction(event: { action: string, item?: DevolutionProjectionCaptureTableModel, selectedItems?: number[] }) {
    if (event.action === 'edit') {
      await this.getDevolutionProjectionDetailById(event.item!.id);
    }
    if (event.action === 'bulk-delete' && event.selectedItems) {
      await this.deleteDetail(event.selectedItems);
    }
  }

  async onFilterChange(event: { filters: any }) {
    const filters = event.filters.map((value: any, index: any): any => ({ value, index }))
      .sort((a: any, b: any) => b.index - a.index).map((item: any) => item.value);
    this.filtersArticle = this.removeDuplicates(filters);
    this.filtersArticle.forEach((filter: any) => this.processFilter(filter));
    this.updateInputs();
  }

  async onPageChange(event: { page: number; pageSize: number; sortField: string | undefined; sortOrder: number },
    context: 'capture' | 'selection') {
    if (context === 'capture') {
      this.page = event.page;
      this.limit = event.pageSize;
      this.columnSort = event.sortField || null;
      this.ascendant = event.sortOrder === 1;
      await this.getDevolutionProjectionDetailsForCapture();
    }

    if (context === 'selection') {
      this.pageArticle = event.page;
      this.limitArticle = event.pageSize;
      this.columnSortArticle = event.sortField || null;
      this.ascendantArticle = event.sortOrder === 1;
      await this.getArticlesForSelection();
    }
  }

  async openArticleSelectionModal() {
    this.filtersArticle = [];
    this.updateInputs();
    await Promise.all([
      this.getColumnsForArticleSelection(),
      this.getArticlesForSelection()
    ]);
    this.articleForSelectionModalComponent?.show();
  }

  processFilter(filter: any): void {
    const { propertyName, valueFilter, valueAlterFilter } = filter;
    if (valueFilter === null || valueFilter === undefined || valueFilter === '' || valueFilter === 0) {
      this.filtersArticle = this.filtersArticle.filter(f => f.propertyName !== propertyName);
    } else {
      const existingFilter = this.filtersArticle.find(f => f.propertyName === propertyName);
      if (existingFilter) {
        existingFilter.valueFilter = valueFilter;
        existingFilter.valueAlterFilter = valueAlterFilter;
      }
    }
  }

  removeDuplicates(filters: any[]): any[] {
    return filters.filter((value, index, self) =>
      index === self.findIndex((t: any) => t.propertyName === value.propertyName)
    );
  }

  updateInputs() {
    this.inputsForArticlesSelection$.next({
      headers: this.headerArticle,
      data: this.dataArticle,
      showCheckbox: true,
      showRecover: true,
      isMultipleSelect: true,
      limitPaginator: true,
      filters: this.filtersArticle,
      actions: [
        {
          label: 'Imágenes',
          icon: 'pi pi-image',
          action: 'view-image'
        }
      ]
    });
  }
}
